import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Device, Maybe, Panel, Zone } from '../../../../types/generated-types';
import {
  DeviceInfo,
  DeviceInformation,
  DevicePowerControl,
} from './device-info';
import { Collapse, Grid, ListItemIcon, Typography } from '@mui/material';
import {
  DeviceAlerts,
  ExpandSectionButton,
  ToggleAlertDetailsButton,
} from './alerts-info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import BorderedSection from '../../shared/borderedSection';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import InsightsIcon from '@mui/icons-material/Insights';
import UndoIcon from '@mui/icons-material/Undo';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import {
  useContainerHasAlerts,
  useDeviceHasAlerts,
} from '../../../system/AlertsManager';

export enum ViewMode {
  NORMAL = 'normal',
  EDIT_UNCHANGED = 'edit_unchanged',
  EDIT = 'edit',
  UPDATING = 'updating',
  AWAITING = 'awaiting',
}

export enum MenuAction {
  EDIT_VALUES = 'edit_values',
  SAVE_CHANGES = 'save_changes',
  REVERT_CHANGES = 'revert_changes',
  CANCEL = 'cancel',
}

interface EditButtonsProps {
  viewMode: ViewMode;
  disabled: boolean;
  handleClick: (param: MenuAction) => void;
}

const EditButtons = (props: EditButtonsProps) => {
  const { viewMode, handleClick, disabled } = props;
  let buttons: MenuAction[];

  switch (viewMode) {
    case ViewMode.NORMAL:
      buttons = [MenuAction.EDIT_VALUES];
      break;
    case ViewMode.EDIT_UNCHANGED:
      buttons = [MenuAction.CANCEL];
      break;
    case ViewMode.EDIT:
      buttons = [MenuAction.SAVE_CHANGES, MenuAction.REVERT_CHANGES];
      break;
    default:
      buttons = [];
  }

  const iconMap = {
    [MenuAction.CANCEL]: <ClearIcon fontSize="inherit" />,
    [MenuAction.EDIT_VALUES]: <EditIcon fontSize="inherit" />,
    [MenuAction.REVERT_CHANGES]: <UndoIcon fontSize="inherit" />,
    [MenuAction.SAVE_CHANGES]: <CheckIcon fontSize="inherit" />,
  };

  return (
    <ButtonGroup
      variant="text"
      color="inherit"
      style={{
        padding: '0px, 5px',
        // height: '100%',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRight: '1px solid rgba(0, 0, 0, 0.23)',
      }}
    >
      {buttons &&
        buttons.map((val) => (
          <Button
            key={`${val}_button`}
            onClick={() => handleClick(val)}
            disabled={disabled}
          >
            <Avatar
              sx={{
                color: 'inherit',
                width: {
                  xs: '16px',
                  sm: '24px',
                  md: '32px',
                },
                height: {
                  xs: '16px',
                  sm: '24px',
                  md: '32px',
                },
                fontSize: {
                  xs: 'medium',
                  sm: 'large',
                  md: 'x-large',
                },
                backgroundColor: 'inherit',
              }}
            >
              {iconMap[val]}
            </Avatar>
          </Button>
        ))}
    </ButtonGroup>
  );
};

const GraphSelectionIcon = (props: {
  buttonLabel: string;
  toggled: boolean;
  showDivider: boolean;
  onClick: (event: any) => void;
  enableExecuteGraphWizard?: boolean;
}) => {
  const {
    buttonLabel = 'Start Graph Selection',
    toggled = false,
    showDivider = false,
    enableExecuteGraphWizard = false,
    onClick,
    ...other
  } = props;

  return (
    <div
      style={
        showDivider
          ? {
              padding: '5px',
              height: '100%',
              width: '35px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: '1px solid rgba(0, 0, 0, 0.23)',
            }
          : {
              width: '35px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
      }
    >
      {toggled ? (
        <QueryStatsIcon
          aria-disabled={enableExecuteGraphWizard}
          style={{
            fontSize: '32px',
            width: '32px',
            height: '32px',
            ...(showDivider
              ? {
                  padding: '0px',
                  cursor: enableExecuteGraphWizard ? 'pointer' : 'not-allowed',
                }
              : { cursor: 'pointer' }),
          }}
          onClick={onClick}
          aria-label={buttonLabel}
          {...other}
        />
      ) : (
        <InsightsIcon
          style={{
            fontSize: '32px',
            width: '32px',
            height: '32px',
            ...(showDivider
              ? {
                  padding: '0px',
                  cursor: 'pointer',
                }
              : { cursor: 'pointer' }),
          }}
          onClick={onClick}
          aria-label={buttonLabel}
          {...other}
        />
      )}
    </div>
  );
};

const CancelGraphIcon = (props: {
  buttonLabel: string;
  toggled: boolean;
  showDivider: boolean;
  onClick: (event: any) => void;
}) => {
  const {
    buttonLabel = 'Cancel Graph Selection',
    toggled = false,
    showDivider = false,
    onClick,
    ...other
  } = props;

  return (
    <div
      style={
        showDivider
          ? {
              padding: '5px',
              height: '100%',
              width: '35px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderRight: '1px solid rgba(0, 0, 0, 0.23)',
            }
          : {
              width: '35px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
      }
    >
      {toggled ? (
        <UndoIcon
          style={{
            fontSize: '32px',
            width: '32px',
            height: '32px',
            ...(showDivider
              ? { padding: '0px', cursor: 'pointer' }
              : { cursor: 'pointer' }),
          }}
          onClick={onClick}
          aria-label={buttonLabel}
          {...other}
        />
      ) : null}
    </div>
  );
};

const PanelIcon = (
  device: Partial<Panel> | Partial<Zone> | undefined | null,
  iconType: string,
  isPoweredOn = false,
  hasAlerts = false,
) => {
  if (device && !isPoweredOn) {
    return <Icon icon={iconType} color="#80BB52" width="30" height="30" />;
  } else if (!device || hasAlerts) {
    // return <Icon sx={{ color: 'red', fontSize: '35px' }} />;
    return <Icon icon={iconType} color="red" width="30" height="30" />;
  } else {
    // return <Icon sx={{ color: '#80BB52', fontSize: '35px' }} />;
    return <Icon icon={iconType} color="#80BB52" width="30" height="30" />;
  }
};

export function PanelFrame({
  title,
  panel,
  zone,
  device: suppliedDevice,
  label,
  iconType,
  infoEntries,
  devicePowerControl,
  children,
  collapsibleContent,
  onCollapsibleContentToggle,
  toggleGraphWizard,
  cancelGraphWizard,
  enableExecuteGraphWizard,
  handleMenuButtonClick = () => {},
  viewMode = ViewMode.NORMAL,
  editingEnabled = true,
  editable = false,
  isPoweredOn = true,
}: {
  title?: string;
  children: React.ReactNode;
  iconType: string;
  panel?: Maybe<Panel> | Partial<Panel>;
  zone?: Maybe<Zone> | Partial<Zone>;
  device?: Maybe<Device> | Partial<Device>;
  label?: Maybe<string>;
  infoEntries: DeviceInformation[] | Record<string, DeviceInformation>;
  devicePowerControl?: DevicePowerControl | undefined | null;
  collapsibleContent?: React.ReactNode;
  onCollapsibleContentToggle?: (isDisplayed: boolean) => void;
  toggleGraphWizard?: () => void;
  cancelGraphWizard?: () => void;
  enableExecuteGraphWizard?: boolean;
  handleMenuButtonClick?: (action: MenuAction) => void;
  viewMode?: ViewMode;
  editingEnabled?: boolean;
  editable?: boolean;
  isPoweredOn?: Maybe<boolean> | undefined;
}) {
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [collapsibleContentExpanded, setCollapsibleContentExpanded] =
    useState(false);
  const [hasAlerts, setHasAlerts] = React.useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleCollapsibleContentToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    const next = !collapsibleContentExpanded;
    setCollapsibleContentExpanded(next);
    onCollapsibleContentToggle?.(next);
  };

  const [toggled, setToggled] = React.useState(false);

  const deviceHasAlerts = useDeviceHasAlerts(suppliedDevice ?? {});
  const zoneHasAlerts = useContainerHasAlerts(zone);
  const panelHasAlerts = useContainerHasAlerts(panel);

  useEffect(() => {
    const shouldSet = deviceHasAlerts || zoneHasAlerts || panelHasAlerts;
    setHasAlerts(shouldSet);
    setAlertsExpanded(shouldSet);
  }, [deviceHasAlerts, zoneHasAlerts, panelHasAlerts]);

  const handleToggleClicked = () => {
    if (toggled) {
      if (enableExecuteGraphWizard) {
        setToggled(!toggled);
        if (toggleGraphWizard) {
          toggleGraphWizard();
        }
      } else {
        // No op by design
      }
    } else {
      setToggled(!toggled);
      if (toggleGraphWizard) {
        toggleGraphWizard();
      }
    }
  };

  const handleCancelGraphingClicked = () => {
    setToggled(false);
    if (cancelGraphWizard) {
      cancelGraphWizard();
    }
  };

  let displayLabel =
    label ?? panel?.name ?? panel?.role ?? zone?.name ?? 'Unnamed';
  const panelTypeLabel = panel?.type ?? '';
  const labelSuffix = panel
    ? panel.type === 'Device' || panel.type === 'GPIO'
      ? '' // no suffix for Device/GPIO panels
      : displayLabel.endsWith(panel.type ?? 'no type')
        ? '' // Loop panel, type already included in label, no suffix
        : panelTypeLabel // Loop panel, type not included in label, append it as suffix
    : '';

  displayLabel = `${displayLabel}${labelSuffix.length > 0 ? ` ${labelSuffix}` : ''}`;

  displayLabel = `${displayLabel}`;

  const showGraphingIcons = !!toggleGraphWizard;

  return panel || zone || suppliedDevice ? (
    <div
      style={{
        width: '100%',
        padding: '0px',
        margin: '0px',
        backgroundColor: '#edfcf4',
        // backgroundColor: (suppliedDevice || panel || zone ? hasAlerts : false)
        //   ? '#ffd5d5'
        //   : '#edfcf4',
        border: hasAlerts ? '2px solid red' : '1px solid darkgray',
        paddingTop: '0px',
        borderRadius: '4px',
      }}
    >
      <div
        style={{
          width: '100%',
          padding: '0px',
          margin: '0px',
          zIndex: '10',
          backgroundColor: 'transparent',
        }}
      >
        <div
          style={{
            padding: '0px',
            margin: '0px',
            width: '100%',
            // backgroundColor: hasAlerts(panel) ? 'lightpink' : 'lightgreen',
            backgroundColor: '#ebf7f6',
            borderRadius: '4px',
          }}
        >
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              borderBottom: hasAlerts ? '2px solid red' : '1px solid darkgray',
              marginTop: '0px',
              marginBottom: '12px',
              backgroundColor: isPoweredOn ? 'transparent' : '#ffffaf',
              padding: '0px 5px',
            }}
          >
            <Grid container>
              <Grid item xs={1}>
                <div
                  style={{
                    display: 'flex',
                    flex: '0 1 auto',
                    position: 'relative',
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '55%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    {PanelIcon(
                      panel || zone,
                      iconType,
                      !!isPoweredOn,
                      hasAlerts,
                    )}
                  </div>
                </div>
              </Grid>
              <Grid item xs={5}>
                <div
                  style={{
                    paddingTop: '8px',
                    marginLeft: '2px',
                    display: 'flex',
                    flex: '1 0 auto',
                  }}
                >
                  <Typography noWrap>{displayLabel}</Typography>
                </div>
              </Grid>
              <Grid item xs={6}>
                <div
                  style={{
                    padding: 0,
                    display: 'flex',
                    flex: '0 1 auto',
                    justifyContent: 'right',
                  }}
                >
                  <ListItemIcon>
                    {editable ? (
                      <EditButtons
                        disabled={!editingEnabled}
                        viewMode={viewMode}
                        handleClick={handleMenuButtonClick}
                      />
                    ) : (
                      <div
                        style={{
                          width: '20px',
                          marginRight: '4px',
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                        }}
                      >
                        &nbsp;
                      </div>
                    )}
                    {showGraphingIcons ? (
                      <div
                        style={{
                          width: cancelGraphWizard && toggled ? '70px' : '35px',
                          minWidth:
                            cancelGraphWizard && toggled ? '70px' : '35px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent:
                            cancelGraphWizard && toggled
                              ? 'flex-start'
                              : 'center',
                          height: '100%',
                          transition:
                            'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
                          overflow: 'hidden',
                        }}
                      >
                        {cancelGraphWizard ? (
                          <div
                            style={{
                              opacity: toggled ? 1 : 0,
                              transform: toggled
                                ? 'translateX(0)'
                                : 'translateX(-10px)',
                              transition:
                                'opacity 0.3s ease-in-out, transform 0.3s ease-in-out, max-width 0.3s ease-in-out',
                              maxWidth: toggled ? '35px' : '0px',
                              overflow: 'hidden',
                              pointerEvents: toggled ? 'auto' : 'none',
                            }}
                          >
                            <CancelGraphIcon
                              buttonLabel="Cancel Graph Selection"
                              toggled={toggled}
                              showDivider={toggled}
                              onClick={handleCancelGraphingClicked}
                            />
                          </div>
                        ) : null}
                        <GraphSelectionIcon
                          buttonLabel="Load Selected Graphs"
                          toggled={toggled}
                          showDivider={true}
                          onClick={handleToggleClicked}
                          enableExecuteGraphWizard={enableExecuteGraphWizard}
                        />
                      </div>
                    ) : null}
                    {collapsibleContent != null ? (
                      <ExpandSectionButton
                        expand={collapsibleContentExpanded}
                        showRightDivider={true}
                        buttonLabel={
                          collapsibleContentExpanded
                            ? 'Hide extra content'
                            : 'Show extra content'
                        }
                        onClick={handleCollapsibleContentToggle}
                      >
                        <UnfoldMoreIcon fontSize="inherit" />
                      </ExpandSectionButton>
                    ) : null}
                    {suppliedDevice ? (
                      <ToggleAlertDetailsButton
                        device={suppliedDevice}
                        expand={alertsExpanded}
                        showRightDivider={true}
                        buttonLabel="Show alerts"
                        onClick={(event) => {
                          event.preventDefault();
                          setAlertsExpanded(!alertsExpanded);
                        }}
                      />
                    ) : panel ? (
                      <ToggleAlertDetailsButton
                        device={panel}
                        expand={alertsExpanded}
                        showRightDivider={true}
                        buttonLabel="Show alerts"
                        onClick={(event) => {
                          event.preventDefault();
                          setAlertsExpanded(!alertsExpanded);
                        }}
                      />
                    ) : zone ? (
                      <ToggleAlertDetailsButton
                        device={zone}
                        expand={alertsExpanded}
                        showRightDivider={true}
                        buttonLabel="Show alerts"
                        onClick={(event) => {
                          event.preventDefault();
                          setAlertsExpanded(!alertsExpanded);
                        }}
                      />
                    ) : null}
                    <ExpandSectionButton
                      expand={expanded}
                      buttonLabel="Show more"
                      onClick={handleExpandClick}
                    >
                      <ExpandMoreIcon />
                    </ExpandSectionButton>
                  </ListItemIcon>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
        <div
          style={{
            padding: '0px',
            margin: '-6px 0px 6px 0px',
            width: '100%',
          }}
        >
          {title ? (
            <BorderedSection
              title={title}
              labelStyle={{ fontSize: '10px' }}
              style={{
                display: 'flex',
              }}
              ccStyle={{
                padding: '4px ',
                margin: '4px',
                flex: '1 1 auto',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              {children}
            </BorderedSection>
          ) : (
            children
          )}
          {collapsibleContent != null ? (
            <Collapse
              in={collapsibleContentExpanded}
              timeout="auto"
              unmountOnExit
            >
              <div
                style={{
                  padding: '0px',
                  margin: '0px',
                  width: '100%',
                }}
              >
                {collapsibleContent}
              </div>
            </Collapse>
          ) : null}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <DeviceInfo
              infoEntries={infoEntries}
              devicePowerControl={devicePowerControl}
            />
          </Collapse>
          <DeviceAlerts
            device={suppliedDevice ?? panel ?? zone ?? {}}
            alertsExpanded={alertsExpanded}
          />
        </div>
      </div>
    </div>
  ) : null;
}
